import { modelSchemas } from "./generated/schemas.ts";
import type {
  DemoDatabase,
  DeliveryPoint,
  EnergyContract,
} from "./generated/models.ts";
import { grantIsWithin, validGrantTopology } from "./access.ts";
import { validateMeasurementRevisions } from "./measurementRevisions.ts";
interface Schema {
  readonly $ref?: string;
  readonly type?: string | readonly string[];
  readonly enum?: readonly unknown[];
  readonly const?: unknown;
  readonly anyOf?: readonly Schema[];
  readonly oneOf?: readonly Schema[];
  readonly allOf?: readonly Schema[];
  readonly properties?: Readonly<Record<string, Schema>>;
  readonly required?: readonly string[];
  readonly additionalProperties?: boolean | Schema;
  readonly minLength?: number;
  readonly maxLength?: number;
  readonly minItems?: number;
  readonly maxItems?: number;
  readonly uniqueItems?: boolean;
  readonly items?: Schema;
  readonly minimum?: number;
  readonly maximum?: number;
  readonly exclusiveMinimum?: number;
  readonly exclusiveMaximum?: number;
  readonly pattern?: string;
}
const schemas: Readonly<Record<string, Schema>> = modelSchemas;
function matches(value: unknown, schema: Schema, depth = 0): boolean {
  if (depth > 40) return false;
  if (schema.$ref) {
    const target = schemas[schema.$ref.split("/").at(-1) ?? ""];
    return Boolean(target && matches(value, target, depth + 1));
  }
  if (schema.enum) return schema.enum.includes(value);
  if ("const" in schema) return schema.const === value;
  if (schema.anyOf || schema.oneOf)
    return (schema.anyOf ?? schema.oneOf ?? []).some((s) =>
      matches(value, s, depth + 1),
    );
  if (schema.allOf)
    return schema.allOf.every((part) => matches(value, part, depth + 1));
  if (schema.type && typeof schema.type !== "string")
    return schema.type.some((type) =>
      matches(value, { ...schema, type }, depth + 1),
    );
  if (schema.type === "null") return value === null;
  if (schema.type === "string")
    return (
      typeof value === "string" &&
      (schema.minLength === undefined || value.length >= schema.minLength) &&
      (schema.maxLength === undefined || value.length <= schema.maxLength) &&
      (schema.pattern === undefined || new RegExp(schema.pattern).test(value))
    );
  if (schema.type === "boolean") return typeof value === "boolean";
  if (schema.type === "number" || schema.type === "integer")
    return (
      typeof value === "number" &&
      Number.isFinite(value) &&
      (schema.type !== "integer" || Number.isSafeInteger(value)) &&
      (schema.minimum === undefined || value >= schema.minimum) &&
      (schema.maximum === undefined || value <= schema.maximum) &&
      (schema.exclusiveMinimum === undefined ||
        value > schema.exclusiveMinimum) &&
      (schema.exclusiveMaximum === undefined || value < schema.exclusiveMaximum)
    );
  if (schema.type === "array")
    return (
      Array.isArray(value) &&
      (schema.minItems === undefined || value.length >= schema.minItems) &&
      (schema.maxItems === undefined || value.length <= schema.maxItems) &&
      (!schema.uniqueItems ||
        new Set(value.map((item) => JSON.stringify(item))).size ===
          value.length) &&
      Boolean(
        schema.items &&
        value.every((v) => matches(v, schema.items ?? {}, depth + 1)),
      )
    );
  if (schema.type === "object") {
    if (!value || typeof value !== "object" || Array.isArray(value))
      return false;
    const record = value as Record<string, unknown>;
    return (
      (schema.required ?? []).every((key) => key in record) &&
      Object.entries(record).every(([key, v]) =>
        schema.properties?.[key]
          ? matches(v, schema.properties[key], depth + 1)
          : typeof schema.additionalProperties === "object"
            ? matches(v, schema.additionalProperties, depth + 1)
            : schema.additionalProperties !== false,
      )
    );
  }
  return false;
}
export function matchesModel(
  name: keyof typeof modelSchemas,
  value: unknown,
): boolean {
  return matches(value, modelSchemas[name]);
}

/** Un contrat peut être incomplet, mais ses valeurs connues restent cohérentes. */
export function isValidEnergyContract(
  contract: EnergyContract | undefined,
  energy: DeliveryPoint["energy"],
): boolean {
  if (!contract) return true;
  return !(
    (contract.validFrom !== undefined &&
      !Number.isFinite(new Date(contract.validFrom).getTime())) ||
    (contract.validUntil !== undefined &&
      !Number.isFinite(new Date(contract.validUntil).getTime())) ||
    (contract.validFrom !== undefined &&
      contract.validUntil !== undefined &&
      new Date(contract.validFrom) >= new Date(contract.validUntil)) ||
    (contract.subscribedPower !== undefined && contract.subscribedPower <= 0) ||
    new Set(contract.scenarios.map((scenario) => scenario.subscribedPower))
      .size !== contract.scenarios.length ||
    (contract.scenarios.length > 0 &&
      (energy !== "electricity" ||
        !contract.scenarios.some(
          (scenario) => scenario.subscribedPower === contract.subscribedPower,
        ))) ||
    contract.scenarios.some(
      (scenario) =>
        scenario.subscribedPower <= 0 ||
        scenario.annualSubscriptionEur < 0 ||
        scenario.penaltiesEur < 0,
    )
  );
}

/** Full imports and canonical mutations must pass structure AND relational invariants. */
export function isValidDemoDatabase(value: unknown): value is DemoDatabase {
  if (!matchesModel("DemoDatabase", value)) return false;
  // The generated schema above establishes every property used below.
  const db = value as DemoDatabase;
  for (const [key, items] of Object.entries(db)) {
    if (key === "siteTags" || key === "performancePeriods") continue;
    const ids = items.map((item) => ("id" in item ? item.id : undefined));
    if (
      ids.some((id) => typeof id !== "string" || !id) ||
      new Set(ids).size !== ids.length
    )
      return false;
  }
  const brandExists = (id: string) => db.brands.some((b) => b.id === id);
  const siteMatches = (id: string, brand: string) =>
    db.sites.some((s) => s.id === id && s.brandId === brand);
  if (
    db.performancePeriods?.some(
      (period) =>
        !db.sites.some((site) => site.id === period.siteId) ||
        new Date(period.periodEnd) <= new Date(period.periodStart),
    )
  )
    return false;
  if (db.companies.some((c) => !brandExists(c.brandId))) return false;
  if (
    db.sites.some(
      (s) =>
        !db.companies.some(
          (c) => c.id === s.companyId && c.brandId === s.brandId,
        ) ||
        s.dataAccess === "context" ||
        (() => {
          const configuration = s.analysisConfiguration;
          if (!configuration) return false;
          const validRange = (start?: string, end?: string) =>
            !start ||
            !end ||
            (Number.isFinite(new Date(start).getTime()) &&
              Number.isFinite(new Date(end).getTime()) &&
              new Date(start) < new Date(end));
          const surfaces = [
            configuration.usefulSurfaceM2,
            configuration.heatedSurfaceM2,
            configuration.cooledSurfaceM2,
          ].filter((value): value is number => value !== undefined);
          if (
            surfaces.some((surface) => surface <= 0) ||
            (configuration.heatedSurfaceM2 !== undefined &&
              configuration.usefulSurfaceM2 !== undefined &&
              configuration.heatedSurfaceM2 > configuration.usefulSurfaceM2) ||
            (configuration.cooledSurfaceM2 !== undefined &&
              configuration.usefulSurfaceM2 !== undefined &&
              configuration.cooledSurfaceM2 > configuration.usefulSurfaceM2) ||
            (configuration.annualCoverageRatio !== undefined &&
              (configuration.annualCoverageRatio < 0 ||
                configuration.annualCoverageRatio > 1)) ||
            (configuration.heatingSetpointC !== undefined &&
              configuration.coolingSetpointC !== undefined &&
              configuration.heatingSetpointC >= configuration.coolingSetpointC)
          )
            return true;
          if (
            new Set(configuration.closures.map((closure) => closure.id))
              .size !== configuration.closures.length ||
            configuration.closures.some(
              (closure) => !validRange(closure.startsAt, closure.endsAt),
            )
          )
            return true;
          const references = [
            configuration.thermalReference,
            configuration.beforeThermalReference,
            configuration.afterThermalReference,
          ].filter((value) => value !== undefined);
          return (
            !validRange(
              configuration.beforePeriodStart,
              configuration.beforePeriodEnd,
            ) ||
            !validRange(
              configuration.afterPeriodStart,
              configuration.afterPeriodEnd,
            ) ||
            references.some(
              (reference) =>
                reference.baseloadKw < 0 ||
                reference.sensitivityKwhM2Dju < 0 ||
                !validRange(reference.periodStart, reference.periodEnd),
            )
          );
        })(),
    )
  )
    return false;
  if (
    db.deliveryPoints.some(
      (p) =>
        !siteMatches(p.siteId, p.brandId) ||
        !isValidEnergyContract(p.energyContract, p.energy),
    )
  )
    return false;
  const memberships = db.users.flatMap((u) => u.memberships);
  if (
    db.companies.some(
      (c) =>
        c.createdByMembershipId &&
        !memberships.some(
          (m) => m.id === c.createdByMembershipId && m.brandId === c.brandId,
        ),
    )
  )
    return false;
  if (new Set(memberships.map((m) => m.id)).size !== memberships.length)
    return false;
  if (
    db.users.some(
      (u) =>
        new Set(u.memberships.map((m) => m.brandId)).size !==
        u.memberships.length,
    )
  )
    return false;
  for (const member of memberships) {
    if (
      !brandExists(member.brandId) ||
      new Set(member.grants.map((g) => g.id)).size !== member.grants.length
    )
      return false;
    if (
      member.invitedByMembershipId &&
      member.invitedByMembershipId !== "platform-super-admin" &&
      !memberships.some(
        (m) =>
          m.id === member.invitedByMembershipId &&
          m.brandId === member.brandId &&
          m.role === "manager",
      )
    )
      return false;
    for (const grant of member.grants) {
      if (!validGrantTopology(db, member.brandId, grant)) return false;
      if (grant.sourceGrantId) {
        const sourceMember = memberships.find(
          (m) =>
            m.id === grant.assignedByMembershipId &&
            m.brandId === member.brandId &&
            m.role === "manager",
        );
        const source = sourceMember?.grants.find(
          (g) => g.id === grant.sourceGrantId && !g.sourceGrantId,
        );
        if (
          member.role !== "client" ||
          !source ||
          !grantIsWithin(source, grant, db, member.brandId)
        )
          return false;
      }
    }
  }
  if (
    db.siteAreas.some(
      (a) =>
        !siteMatches(a.siteId, a.brandId) ||
        (a.parentAreaId &&
          !db.siteAreas.some(
            (p) =>
              p.id === a.parentAreaId &&
              p.siteId === a.siteId &&
              p.brandId === a.brandId,
          )),
    )
  )
    return false;
  if (
    db.tags.some((t) => !brandExists(t.brandId)) ||
    db.siteTags.some(
      (t) =>
        !db.sites.some(
          (s) =>
            s.id === t.siteId &&
            db.tags.some(
              (tag) => tag.id === t.tagId && tag.brandId === s.brandId,
            ),
        ),
    )
  )
    return false;
  if (
    db.meters.some(
      (m) =>
        !siteMatches(m.siteId, m.brandId) ||
        (m.deliveryPointId &&
          !db.deliveryPoints.some(
            (p) => p.id === m.deliveryPointId && p.siteId === m.siteId,
          )) ||
        (m.siteAreaId &&
          !db.siteAreas.some(
            (a) => a.id === m.siteAreaId && a.siteId === m.siteId,
          )) ||
        (m.parentMeterId &&
          !db.meters.some(
            (p) => p.id === m.parentMeterId && p.siteId === m.siteId,
          )),
    )
  )
    return false;
  if (
    db.equipment.some(
      (e) =>
        !siteMatches(e.siteId, e.brandId) ||
        (e.meterId &&
          !db.meters.some(
            (m) => m.id === e.meterId && m.siteId === e.siteId,
          )) ||
        (e.siteAreaId &&
          !db.siteAreas.some(
            (a) => a.id === e.siteAreaId && a.siteId === e.siteId,
          )),
    )
  )
    return false;
  if (
    db.series.some(
      (s) =>
        !db.deliveryPoints.some((p) => p.id === s.deliveryPointId) ||
        !validateMeasurementRevisions(s),
    )
  )
    return false;
  const workflows = [
    ...db.anomalies,
    ...db.recommendations,
    ...db.works,
    ...db.reports,
  ];
  const sourceMatches = (
    id: string,
    siteId: string,
    visited: Set<string>,
  ): boolean => {
    if (visited.has(id)) return false;
    const series = db.series.find((s) => s.id === id);
    if (series)
      return db.deliveryPoints.some(
        (p) => p.id === series.deliveryPointId && p.siteId === siteId,
      );
    const point = db.deliveryPoints.find((p) => p.id === id);
    if (point) return point.siteId === siteId;
    // Une station météo de la plateforme (`weather-…`) peut être citée comme
    // source : elle est hors marque, sans rattachement à un Site.
    if (id.startsWith("weather-")) return true;
    const workflow = workflows.find((w) => w.id === id && w.siteId === siteId);
    return Boolean(
      workflow &&
      (workflow.sourceIds ?? []).every((sourceId) =>
        sourceMatches(sourceId, siteId, new Set([...visited, id])),
      ),
    );
  };
  if (
    new Set(workflows.map((w) => w.id)).size !== workflows.length ||
    workflows.some(
      (w) =>
        !db.sites.some(
          (s) =>
            s.id === w.siteId &&
            s.companyId === w.companyId &&
            s.brandId === w.brandId,
        ) ||
        w.sourceIds?.some(
          (id) => !sourceMatches(id, w.siteId, new Set([w.id])),
        ),
    )
  )
    return false;
  return db.audit.every(
    (e) => e.brandId === undefined || brandExists(e.brandId),
  );
}
